import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../design-system';

const Terminal: React.FC = () => {
  const [history, setHistory] = useState([
    { type: 'system', text: '🚀 Welcome to Incupie 0.1.0' },
    { type: 'system', text: 'AI for 3D simulation, media & code generation' },
    { type: 'user', text: 'incupie run incupie-arc-256b' },
    { type: 'system', text: '✓ Model loaded successfully (1.8s)' },
    { type: 'user', text: 'incupie generate --type 3d "modern office space"' },
    { type: 'system', text: '✓ 3D scene generated successfully' },
    { type: 'system', text: '📁 Output: /workspace/office_scene.glb' },
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [history]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    setHistory(h => [...h, { type: 'user', text: input }]);
    setIsProcessing(true);
    setTimeout(() => {
      // Simulate AI response
      setHistory(h => [...h, { type: 'system', text: getAIResponse(input) }]);
      setIsProcessing(false);
    }, 1200);
    setInput('');
  };
  function getAIResponse(cmd: string) {
    if (/help|--help|\?/.test(cmd)) return '📋 Available: 3D simulation, media & code generation\n💡 Commands: run, generate, code, status, clear, exit';
    if (/clear/.test(cmd)) {
      setHistory([{ type: 'system', text: '🚀 Welcome to Incupie 0.1.0' }]);
      return '';
    }
    if (/status/.test(cmd)) return '🟢 System Status: Online | Models: Ready | Memory: 78% | GPU: Available';
    if (/exit|quit/.test(cmd)) return '👋 Session ended. Thanks for using Incupie CLI!';
    if (/generate/i.test(cmd)) return '✓ Content generated successfully\n📁 Output saved to: /outputs/' + Date.now() + '.png';
    if (/run model/i.test(cmd)) return '✓ AI model loaded and ready\n🧠 Model: Incupie-V2 | Performance: Optimized';
    if (/hello|hi|hey/i.test(cmd)) return '👋 Hello! I\'m your AI assistant. How can I help you create something amazing today?';
    return '🤖 AI: ' + (cmd.endsWith('?') ? 'Great question! Here\'s what I think...' : 'Command processed successfully!');
  } return (
    <div className="w-full max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
      <Card className="overflow-hidden shadow-2xl" padding="sm">
        {/* Terminal Header */}
        <div className="flex items-center justify-between px-3 py-2 md:px-4 md:py-3 border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-red-500"></div>
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-yellow-500"></div>
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="text-xs font-medium text-white/80 md:text-sm">Incupie 0.1.0</div>
          <div className="w-12 md:w-16"></div>
        </div>
        {/* Interactive Terminal Content */}        <div ref={scrollRef} className="h-64 p-3 space-y-2 overflow-y-auto font-mono text-xs md:p-6 md:text-sm md:h-72">
          {history.map((item, idx) => (
            <div key={idx} className={`flex ${item.type === 'user' ? 'text-primary-400' : 'text-green-400'}`}>
              <span className="flex-shrink-0 mr-2 font-bold">
                {item.type === 'user' ? '$ ' : '# '}
              </span>
              <span className="break-words whitespace-pre-line text-white/90">{item.text}</span>
            </div>
          ))}
          {isProcessing && (
            <div className="flex text-green-400">
              <span className="flex-shrink-0 mr-2 font-bold"># </span>
              <span className="flex items-center text-white/90 animate-pulse">
                Processing... <span className="ml-2 animate-spin">⚡</span>
              </span>
            </div>
          )}
        </div>{/* Input Bar */}
        <form onSubmit={handleSubmit} className="flex items-center px-3 py-2 border-t bg-dark-700 md:px-4 md:py-3 border-white/10">
          <span className="mr-2 text-xs font-bold text-primary-400 md:text-sm">$</span>
          <input ref={inputRef}
            type="text"
            className="flex-1 text-xs text-white bg-transparent outline-none placeholder-white/40 md:text-sm"
            placeholder="Type a command or ask a question..."
            value={input}
            onChange={handleInput}
            disabled={isProcessing}
            autoFocus
          />        </form>
      </Card>
    </div>
  );
};

export default Terminal;
